import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { moduleStats, moduleHasMatch } from '../permissionModel';
import { AMBER, BLUE, GRAY_DENIED, GREEN, SectionLabel } from '../styles';

// ---------------------------------------------------------------------------
// Rail de módulos (coluna A do grid) — lista de módulos com contador
// concedidas/total e a barra segmentada: azul = concedidas comuns, âmbar =
// sensíveis concedidas, cinza escuro = sensíveis negadas; o restante do
// trilho (cinza claro) são as comuns negadas. Sem checkbox de seleção em
// massa por módulo — decisão de produto do handoff (evita grandes blocos de
// alterações não salvas).
// ---------------------------------------------------------------------------

// Fundo/borda da coluna moram na RailColumn (styles.js) desde a v2 do
// handoff — este bloco divide a coluna com o RailSummary.
const Rail = styled.div`
   flex: 1;
   min-height: 0;
`;

const RailHead = styled.div`
   padding: 12px 14px 8px;
`;

const Legend = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 4px 10px;
   margin-top: 6px;
   font-size: 10.5px;
   font-weight: 600;
   color: #7a7a7a;
`;

const LegendDot = styled.span`
   display: inline-block;
   width: 9px;
   height: 4px;
   border-radius: 999px;
   margin-right: 4px;
   background: ${({ $color }) => $color};
`;

const List = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   padding: 0 8px 12px;
`;

// Item do rail é controle local com estado de domínio (G.2: fica fora do
// kit) — e por isso declara o próprio :hover com background (G.4.1).
const Item = styled.button`
   appearance: none;
   border: 0;
   font-family: inherit;
   text-align: left;
   width: 100%;
   padding: 8px;
   border-radius: 10px;
   cursor: pointer;
   background: ${({ $active }) => ($active ? BLUE.bgActive : 'transparent')};
   opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? BLUE.bgActive : BLUE.bgRailHover};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

const ItemTop = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   min-width: 0;

   svg {
      width: 18px;
      height: 18px;
      flex: 0 0 auto;
      color: ${({ $active, theme }) =>
         $active ? theme.colors.actionWrite : '#8a8a8a'};
   }
`;

const ItemName = styled.span`
   flex: 1;
   min-width: 0;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   font-size: 13.5px;
   font-weight: ${({ $active }) => ($active ? 800 : 700)};
   color: ${({ $active }) => ($active ? BLUE.text : '#333')};
`;

const ItemCount = styled.span`
   font-size: 11px;
   font-weight: 700;
   font-variant-numeric: tabular-nums;
   color: ${({ $tone }) => {
      if ($tone === 'full') return GREEN.text;
      if ($tone === 'zero') return '#a0a0a0';
      return '#666';
   }};
`;

const Track = styled.div`
   display: flex;
   height: 4px;
   margin-top: 6px;
   border-radius: 999px;
   overflow: hidden;
   background: rgba(0, 0, 0, 0.08);
`;

const Segment = styled.span`
   flex: 0 0 ${({ $fraction }) => $fraction * 100}%;
   background: ${({ $color }) => $color};
`;

export default function ModuleRail({ modules, granted, selected, onSelect, query }) {
   return (
      <Rail>
         <RailHead>
            <SectionLabel>Módulos</SectionLabel>
            <Legend>
               <span>
                  <LegendDot $color="#4098ef" />
                  Concedidas
               </span>
               <span>
                  <LegendDot $color={AMBER.base} />
                  Sensíveis
               </span>
               <span>
                  <LegendDot $color={GRAY_DENIED} />
                  Negadas
               </span>
            </Legend>
         </RailHead>

         <List>
            {modules.map((mod) => {
               const stats = moduleStats(mod, granted);
               const Icon = mod.icon;
               const active = mod.key === selected;
               const dimmed = Boolean(query) && !moduleHasMatch(mod, query);

               let tone = 'partial';
               if (stats.total > 0 && stats.granted === stats.total) tone = 'full';
               else if (stats.granted === 0) tone = 'zero';

               return (
                  <Item
                     key={mod.key}
                     type="button"
                     $active={active}
                     $dimmed={dimmed}
                     onClick={() => onSelect(mod.key)}
                  >
                     <ItemTop $active={active}>
                        <Icon />
                        <ItemName $active={active}>{mod.label}</ItemName>
                        <ItemCount $tone={tone}>
                           {stats.granted}/{stats.total}
                        </ItemCount>
                     </ItemTop>
                     <Track>
                        {stats.commonGranted > 0 && (
                           <Segment
                              $fraction={stats.commonGranted / stats.total}
                              $color="#4098ef"
                              title={`${stats.commonGranted} concedidas`}
                           />
                        )}
                        {stats.sensitiveGranted > 0 && (
                           <Segment
                              $fraction={stats.sensitiveGranted / stats.total}
                              $color={AMBER.base}
                              title={`${stats.sensitiveGranted} sensíveis concedidas`}
                           />
                        )}
                        {stats.sensitiveDenied > 0 && (
                           <Segment
                              $fraction={stats.sensitiveDenied / stats.total}
                              $color={GRAY_DENIED}
                              title={`${stats.sensitiveDenied} sensíveis não concedidas`}
                           />
                        )}
                     </Track>
                  </Item>
               );
            })}
         </List>
      </Rail>
   );
}

ModuleRail.propTypes = {
   modules: PropTypes.arrayOf(PropTypes.object).isRequired,
   granted: PropTypes.instanceOf(Set).isRequired,
   selected: PropTypes.string,
   onSelect: PropTypes.func.isRequired,
   query: PropTypes.string,
};

ModuleRail.defaultProps = {
   selected: null,
   query: '',
};
